<template>
  <div class="form-group position-relative" :class="label ? 'labelled' : ''">
    <label v-if="label" :for="id">{{ label }}</label>
    <div :class="hasIcon(icon)">
      <span v-if="iconDir === 'left'" class="input-group-text">
        <i :class="getIcon(icon)"></i>
      </span>
      <input
        ref="input"
        :id="id"
        :type="type"
        class="form-control"
        :class="getClasses(size, success, error, iconDir)"
        :name="name"
        :value="currentValue"
        :placeholder="placeholder"
        :required="isRequired"
        autocomplete="off"
        @input="handleInput"
        @focus="handleFocus"
        @keydown.down.prevent="highlightNext"
        @keydown.up.prevent="highlightPrev"
        @keydown.enter.prevent="selectHighlighted"
        @keydown.esc="closeSuggestions"
        @blur="handleBlur"
      />

      <span v-if="iconDir === 'right'" class="input-group-text">
        <i :class="getIcon(icon)"></i>
      </span>
    </div>

    <ul
      v-if="showSuggestions && suggestions.length > 0"
      class="autocomplete-list list-group position-absolute w-100 shadow-sm"
      style="z-index: 9999;"
    >
      <li
        v-for="(item, index) in suggestions"
        :key="getItemKey(item, index)"
        class="list-group-item list-group-item-action"
        :class="{ active: index === highlightedIndex }"
        @mousedown.prevent="selectItem(item)"
        @mouseenter="highlightedIndex = index"
      >
        {{ getItemLabel(item) }}
      </li>
    </ul>
  </div>
</template>

<script>
export default {
  name: "SoftInputAutocomplete",
  props: {
    size: {
      type: String,
      default: "default",
    },
    success: {
      type: Boolean,
      default: false,
    },
    error: {
      type: Boolean,
      default: false,
    },
    icon: {
      type: String,
      default: "",
    },
    iconDir: {
      type: String,
      default: "",
    },
    name: {
      type: String,
      default: "",
    },
    id: {
      type: String,
      default: "",
    },
    value: {
      type: String,
      default: "",
    },
    modelValue: {
      type: String,
      default: "",
    },
    placeholder: {
      type: String,
      default: "Type here...",
    },
    type: {
      type: String,
      default: "text",
    },
    isRequired: {
      type: Boolean,
      default: false,
    },

    fetchData: {
      type: Function,
      default: null,
    },

    // URL del servicio para mantener compatibilidad con usos existentes
    serviceUrl: {
      type: String,
      default: "",
    },

    // Nombre del parámetro que se enviará al servicio
    queryParam: {
      type: String,
      default: "q",
    },

    // Tiempo de espera antes de consultar
    debounceMs: {
      type: Number,
      default: 300,
    },

    // Longitud mínima para empezar a buscar
    minChars: {
      type: Number,
      default: 2,
    },

    // Campo a mostrar si el servicio devuelve objetos
    labelField: {
      type: String,
      default: "label",
    },

    // Campo id si el servicio devuelve objetos
    valueField: {
      type: String,
      default: "id",
    },

    label: {
      type: String,
      default: "",
    },
    // Si quieres pasar cabeceras extra
    requestHeaders: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      suggestions: [],
      showSuggestions: false,
      highlightedIndex: -1,
      debounceTimer: null,
      isLoading: false,
      lastQuery: "",
      autocompleteSelection: null,
    };
  },

  computed: {
    currentValue() {
      return this.modelValue !== undefined && this.modelValue !== null
        ? this.modelValue
        : this.value;
    },
  },

  methods: {
    getClasses(size, success, error, iconDir) {
      let sizeValue = size ? `form-control-${size}` : "";
      let isValidValue = "";

      if (error) {
        isValidValue = "is-invalid";
      } else if (success) {
        isValidValue = "is-valid";
      }

      if (iconDir==="left") {
        sizeValue += " input-group-left-icon";
      } else if (iconDir === "right") {
        sizeValue += " input-group-right-icon";
      }
      return `${sizeValue} ${isValidValue}`.trim();
    },

    getIcon(icon) {
      return icon || null;
    },

    hasIcon(icon) {
      return icon ? "input-group" : null;
    },

    handleInput(event) {
      const val = event.target.value;

      this.autocompleteSelection = null;

      this.$emit("update:modelValue", val);
      this.$emit("input", val);

      this.highlightedIndex = -1;

      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }

      if (!val || val.trim().length < this.minChars) {
        this.suggestions = [];
        this.showSuggestions = false;
        return;
      }

      this.debounceTimer = setTimeout(() => {
        this.fetchSuggestions(val);
      }, this.debounceMs);
    },

    handleFocus() {
      if (this.suggestions.length > 0) {
        this.showSuggestions = true;
      }
    },

    handleBlur() {
      // Pequeño retraso para permitir click sobre sugerencia
      setTimeout(() => {
        this.selectCurrentSuggestionOnBlur();
        this.closeSuggestions();
      }, 150);
    },

    closeSuggestions() {
      this.showSuggestions = false;
      this.highlightedIndex = -1;
    },

    selectCurrentSuggestionOnBlur() {
      if (this.suggestions.length === 0) {
        return;
      }

      const currentValue = this.currentValue?.trim();

      if (!currentValue) {
        return;
      }

      const exactMatch = this.suggestions.find(
        (item) => this.getItemLabel(item) === currentValue
      );

      if (exactMatch) {
        this.selectItem(exactMatch);
        return;
      }

      if (this.suggestions.length === 1) {
        this.selectItem(this.suggestions[0]);
        return;
      }
    },
    
    async fetchSuggestions(searchText) {
      try {
        this.isLoading = true;
        this.lastQuery = searchText;

        let result;

        if (typeof this.fetchData === "function") {
          result = await this.fetchData({
            search: searchText,
            queryParam: this.queryParam,
          });
        } else {
          if (!this.serviceUrl) {
            throw new Error("SoftInputAutocomplete requiere fetchData o serviceUrl");
          }

          const url = new URL(this.serviceUrl, window.location.origin);
          url.searchParams.set(this.queryParam, searchText);

          const response = await fetch(url.toString(), {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              ...this.requestHeaders,
            },
          });

          if (!response.ok) {
            throw new Error(`Error HTTP ${response.status}`);
          }

          result = await response.json();
        }

        const items = this.normalizeItems(result);

        // Evita pintar resultados antiguos si el usuario siguió escribiendo
        if (this.lastQuery !== searchText) {
          return;
        }

        if (this.tryInlineAutocomplete(items, searchText)) {
          return;
        }

        this.suggestions = items;
        this.showSuggestions = items.length > 0;
        this.highlightedIndex = -1;

        this.$emit("suggestions-loaded", items);
      } catch (error) {
        console.error("Error cargando sugerencias:", error);
        this.suggestions = [];
        this.showSuggestions = false;
        this.$emit("autocomplete-error", error);
      } finally {
        this.isLoading = false;
      }
    },

    normalizeItems(result) {
      if (Array.isArray(result)) {
        return result;
      }

      if (Array.isArray(result?.data)) {
        return result.data;
      }

      if (Array.isArray(result?.items)) {
        return result.items;
      }

      if (Array.isArray(result?.rows)) {
        return result.rows;
      }

      return [];
    },

    getItemLabel(item) {
      if (typeof item === "string") return item;
      if (item && typeof item === "object") {
        return item[this.labelField] ?? item.label ?? item.nombre ?? item.name ?? "";
      }
      return "";
    },

    getItemValue(item) {
      if (typeof item === "string") return item;
      if (item && typeof item === "object") {
        return item[this.valueField] ?? this.getItemLabel(item);
      }
      return "";
    },

    getItemKey(item, index) {
      if (typeof item === "string") return `${item}-${index}`;
      if (item && typeof item === "object") {
        return item[this.valueField] ?? index;
      }
      return index;
    },

    tryInlineAutocomplete(items, searchText) {
      if (items.length !== 1) {
        return false;
      }

      const item = items[0];
      const label = this.getItemLabel(item);
      const normalizedSearch = searchText?.trim() ?? "";

      if (!label || !normalizedSearch) {
        return false;
      }

      if (!label.toLowerCase().startsWith(normalizedSearch.toLowerCase())) {
        return false;
      }

      if (label === searchText) {
        return false;
      }

      this.autocompleteSelection = {
        start: searchText.length,
        end: label.length,
      };

      this.$emit("update:modelValue", label);
      this.$emit("input", label);
      this.$emit("autocompleted", item);

      this.suggestions = [item];
      this.showSuggestions = false;
      this.highlightedIndex = -1;

      this.$nextTick(() => {
        const input = this.$refs.input;

        if (
          !input ||
          this.lastQuery !== searchText ||
          this.currentValue !== label ||
          document.activeElement !== input
        ) {
          return;
        }

        input.setSelectionRange(
          this.autocompleteSelection.start,
          this.autocompleteSelection.end
        );
      });

      return true;
    },

    selectItem(item) {
      const label = this.getItemLabel(item);

      this.autocompleteSelection = null;
      this.$emit("update:modelValue", label);
      this.$emit("input", label);
      this.$emit("selected", item);

      this.suggestions = [];
      this.showSuggestions = false;
      this.highlightedIndex = -1;
    },

    highlightNext() {
      if (!this.showSuggestions || this.suggestions.length === 0) return;

      if (this.highlightedIndex < this.suggestions.length - 1) {
        this.highlightedIndex++;
      } else {
        this.highlightedIndex = 0;
      }
    },

    highlightPrev() {
      if (!this.showSuggestions || this.suggestions.length === 0) return;

      if (this.highlightedIndex > 0) {
        this.highlightedIndex--;
      } else {
        this.highlightedIndex = this.suggestions.length - 1;
      }
    },

    selectHighlighted() {
      if (
        this.showSuggestions &&
        this.highlightedIndex >= 0 &&
        this.highlightedIndex < this.suggestions.length
      ) {
        this.selectItem(this.suggestions[this.highlightedIndex]);
      }
    },
  },

  beforeUnmount() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
  },
};
</script>

<style scoped>
.autocomplete-list {
  top: 100%;
  left: 0;
  margin-top: 2px;
  max-height: 240px;
  overflow-y: auto;
  border-radius: 0.375rem;
}
.autocomplete-list li {
  margin:0;
  padding:3px;
  font-size:12px;
  line-height:18px;
  text-align:left;
}
.labelled {
  .autocomplete-list {
    max-width: calc(100% - 152px);
    margin-left: 150px;
  }
}
</style>